import type { CreateTaskBody, EmailPreviewDTO, EmailRecipientDTO, EmailTemplateDTO, SendEmailBody, TaskDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePlus, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge, Button, Field, Input, Select, SidePanel, Spinner, Textarea, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { api, errorMessage } from '../../lib/api';
import { useMailboxes } from '../settings/sections/MailboxesCard';
import { browserTimeZone, followUpBody } from '../tasks/entryModel';
import { FollowUpFields, useFollowUp } from '../tasks/FollowUpFields';
import { invalidateTaskQueries } from '../tasks/taskQueries';
import { DeliveryWarning } from './DeliveryWarning';
import { SaveAsTemplateDialog } from './SaveAsTemplateDialog';

export type EmailComposerProps = {
  businessId: string;
  businessName?: string;
  contactId?: string | null;
  templateId?: string | null;
  initialSubject?: string;
  initialBody?: string;
  onClose: () => void;
  onSent?: () => void;
};

/**
 * Compose and send (or schedule) one email to a lead. The rendered preview comes from the server, so what the
 * salesperson sees is exactly what will be sent — including the compliance footer and unsubscribe link.
 */
export function EmailComposer({
  businessId,
  businessName,
  contactId,
  templateId,
  initialSubject,
  initialBody,
  onClose,
  onSent,
}: EmailComposerProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [recipientId, setRecipientId] = useState<string | null>(contactId ?? null);
  const [template, setTemplate] = useState<string>(templateId ?? '');
  const [subject, setSubject] = useState(initialSubject ?? '');
  const [body, setBody] = useState(initialBody ?? '');
  const [scheduledFor, setScheduledFor] = useState('');
  /** '' = automatic: the server picks the mailbox this lead last heard from, else the least-used one. */
  const [fromMailboxId, setFromMailboxId] = useState('');
  const [showHtml, setShowHtml] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Generated once per composer session: the API derives the idempotency key from it, so a double click or a
  // network retry can never send twice.
  const clientRequestId = useMemo(() => crypto.randomUUID(), []);
  const [followUp, setFollowUp] = useFollowUp({ type: 'follow_up', days: 3 });

  const { data: recipientData } = useQuery({
    queryKey: ['leads', businessId, 'email-recipients'],
    queryFn: ({ signal }) => api.get<{ recipients: EmailRecipientDTO[] }>(`/api/leads/${businessId}/email-recipients`, { signal }),
  });
  const { data: mailboxes } = useMailboxes();
  const { data: templates } = useQuery({
    queryKey: ['templates', 'active'],
    queryFn: ({ signal }) => api.get<{ items: EmailTemplateDTO[] }>('/api/templates?active=true', { signal }),
  });

  const recipients = recipientData?.recipients ?? [];
  const myMailboxes = (mailboxes?.items ?? []).filter((mailbox) => mailbox.isMine && mailbox.status === 'active');
  const fromMailbox = myMailboxes.find((mailbox) => mailbox.id === fromMailboxId) ?? null;
  // With a single mailbox "Automatic" can only mean that one, so name it.
  const sendingFrom = fromMailbox ?? (myMailboxes.length === 1 ? myMailboxes[0]! : null);

  /** Choosing a template fills empty subject/body from it so the salesperson only edits what needs editing. */
  const chooseTemplate = (templateId: string) => {
    setTemplate(templateId);
    const picked = (templates?.items ?? []).find((item) => item.id === templateId);
    if (!picked) return;
    setSubject((current) => (current.trim() === '' ? picked.subject : current));
    setBody((current) => (current.trim() === '' ? picked.body : current));
  };
  const selected = recipients.find((r) => (r.contactId ?? 'business') === (recipientId ?? '')) ?? recipients[0];
  const pickedTemplate = (templates?.items ?? []).find((item) => item.id === template) ?? null;

  const debouncedSubject = useDebouncedValue(subject, 300);
  const debouncedBody = useDebouncedValue(body, 300);
  const { data: preview, isFetching: previewing } = useQuery({
    queryKey: ['leads', businessId, 'email-preview', selected?.email, template, debouncedSubject, debouncedBody, fromMailbox?.id ?? null],
    queryFn: ({ signal }) =>
      api.post<EmailPreviewDTO>(`/api/leads/${businessId}/emails/preview`, {
        contactId: selected?.contactId ?? null,
        toEmail: selected?.email ?? null,
        templateId: template || null,
        subject: debouncedSubject || undefined,
        body: debouncedBody || undefined,
        emailAccountId: fromMailbox?.id ?? null,
      }, { signal }),
    enabled: Boolean(selected?.email) && (debouncedSubject.trim() !== '' || template !== ''),
  });

  const send = useMutation({
    mutationFn: () =>
      api.post<{ created: boolean }>(`/api/leads/${businessId}/emails`, {
        contactId: selected?.contactId ?? null,
        toEmail: selected?.email ?? null,
        templateId: template || null,
        subject,
        body,
        scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : null,
        clientRequestId,
        emailAccountId: fromMailbox?.id,
      } satisfies SendEmailBody),
    onSuccess: async (result) => {
      toast.success(scheduledFor ? 'Email scheduled' : result.created ? 'Email sent' : 'Email already sent');
      // The email is out either way: a failed follow-up is reported on its own and never undoes the send.
      const next = followUpBody(followUp, followUpTitle, new Date());
      if (next) {
        try {
          await api.post<TaskDTO>('/api/tasks', {
            businessId,
            contactId: selected?.contactId ?? null,
            ...next,
            timeZone: browserTimeZone(),
          } satisfies CreateTaskBody);
          await invalidateTaskQueries(queryClient, businessId);
        } catch (error) {
          toast.error(`Follow-up not scheduled: ${errorMessage(error)}`);
        }
      }
      onSent?.();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const blocking = preview?.eligibility.reasons.filter((reason) => reason.blocking) ?? [];
  const warnings = preview?.eligibility.reasons.filter((reason) => !reason.blocking) ?? [];
  const followUpTitle = `Follow up: ${(preview?.composed.subject ?? subject).trim() || 'email'}`;
  const followUpInvalid = followUp.enabled && followUpBody(followUp, followUpTitle, new Date()) === null;

  return (
    <SidePanel
      open
      onClose={onClose}
      title={`Email ${businessName ?? 'this lead'}`}
      width="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {preview
              ? `To ${preview.composed.toEmail}${preview.composed.toName ? ` (${preview.composed.toName})` : ''}`
              : 'Pick a recipient to see the preview'}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              icon={Send}
              loading={send.isPending}
              disabled={blocking.length > 0 || !selected?.email || subject.trim() === '' || body.trim() === '' || followUpInvalid}
              onClick={() => send.mutate()}
            >
              {scheduledFor ? 'Schedule' : 'Send'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="To">
          <Select
            value={selected ? (selected.contactId ?? 'business') : ''}
            onChange={(event) => setRecipientId(event.target.value)}
            placeholder="Select a recipient"
            options={recipients.map((recipient) => ({
              value: recipient.contactId ?? 'business',
              label: `${recipient.name}${recipient.email ? ` — ${recipient.email}` : ' — no email'}`,
            }))}
          />
        </Field>
        {recipients.some((recipient) => !recipient.eligible) && (
          <ul className="space-y-1 text-xs text-slate-600">
            {recipients
              .filter((recipient) => !recipient.eligible && recipient.reasons.length > 0)
              .map((recipient) => (
                <li key={recipient.contactId ?? 'business'} className="flex items-start gap-1.5">
                  <Badge color="red" size="sm">
                    blocked
                  </Badge>
                  <span>
                    {recipient.name}: {recipient.reasons.map((reason) => reason.message).join('; ')}
                  </span>
                </li>
              ))}
          </ul>
        )}

        {myMailboxes.length > 0 ? (
          <Field
            label="From"
            hint={
              sendingFrom
                ? `Sending from ${sendingFrom.emailAddress} — replies come back to that inbox.`
                : 'Automatic: the mailbox this lead last heard from, otherwise the one you have used least. Replies come back to that inbox.'
            }
          >
            <Select
              value={fromMailboxId}
              onChange={(event) => setFromMailboxId(event.target.value)}
              options={[
                { value: '', label: 'Automatic' },
                ...myMailboxes.map((mailbox) => ({ value: mailbox.id, label: mailbox.displayName ? `${mailbox.displayName} <${mailbox.emailAddress}>` : mailbox.emailAddress })),
              ]}
            />
          </Field>
        ) : (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Sending from the company address. Connect your own mailbox in Settings → Email to send as yourself.
          </p>
        )}
        {preview?.deliveryWarning && <DeliveryWarning message={preview.deliveryWarning} />}

        <Field label="Template" hint="Variables like {{first_name}} are filled from this lead.">
          <Select
            value={template}
            onChange={(event) => chooseTemplate(event.target.value)}
            placeholder="Write manually"
            options={(templates?.items ?? []).map((item) => ({ value: item.id, label: item.name }))}
          />
        </Field>

        <Field label="Subject">
          <Input value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Pizza ovens for {{business_name}}" />
        </Field>
        <Field
          label="Message"
          labelAside={
            can('templates:manage') ? (
              <Button size="sm" variant="ghost" icon={FilePlus} className="-my-1" disabled={subject.trim() === '' || body.trim() === ''} onClick={() => setSavingTemplate(true)}>
                Save as template
              </Button>
            ) : undefined
          }
        >
          <Textarea rows={10} value={body} onChange={(event) => setBody(event.target.value)} placeholder={'Hi {{first_name}},\n\n…'} />
        </Field>

        <Field label="Send later" hint="Leave empty to send now.">
          <Input type="datetime-local" value={scheduledFor} onChange={(event) => setScheduledFor(event.target.value)} />
        </Field>

        <FollowUpFields value={followUp} onChange={setFollowUp} defaultTitle={followUpTitle} />

        {blocking.length > 0 && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">
            <p className="font-medium">This email cannot be sent:</p>
            <ul className="mt-1 list-disc pl-4">
              {blocking.map((reason) => (
                <li key={reason.code}>{reason.message}</li>
              ))}
            </ul>
          </div>
        )}
        {warnings.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
            <ul className="list-disc pl-4">
              {warnings.map((reason) => (
                <li key={reason.code}>{reason.message}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Preview</h3>
            <div className="flex items-center gap-2">
              {previewing && <Spinner className="size-3.5" />}
              <Button size="sm" variant="ghost" onClick={() => setShowHtml((current) => !current)}>
                {showHtml ? 'Plain text' : 'HTML'}
              </Button>
            </div>
          </div>
          {preview ? (
            <>
              <div className="rounded-md border border-slate-200 bg-surface p-3 text-[13px]">
                <p className="mb-2 font-medium text-slate-900">{preview.composed.subject}</p>
                {showHtml ? (
                  <iframe title="Email preview" className="h-72 w-full rounded border border-slate-200 bg-white dark:brightness-90" sandbox="" srcDoc={preview.composed.html} />
                ) : (
                  <pre className="whitespace-pre-wrap wrap-anywhere font-sans text-slate-700">{preview.composed.text}</pre>
                )}
              </div>
              {preview.missing.length > 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  Empty variables: {preview.missing.join(', ')} — the template fallback (if any) is used.
                </p>
              )}
              {preview.unknown.length > 0 && (
                <p className="mt-1 text-xs text-red-700">Unknown variables would be sent literally: {preview.unknown.join(', ')}</p>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-500">Start typing or pick a template to see the rendered email.</p>
          )}
        </div>
      </div>
      {savingTemplate && (
        <SaveAsTemplateDialog
          subject={subject}
          body={body}
          defaultName={(pickedTemplate ? `${pickedTemplate.name} (my version)` : subject.trim()).slice(0, 200)}
          defaultCategory={pickedTemplate?.category ?? 'custom'}
          onClose={() => setSavingTemplate(false)}
          onSaved={(saved) => {
            setTemplate(saved.id);
            setSavingTemplate(false);
          }}
        />
      )}
    </SidePanel>
  );
}
